import React, { FC } from 'react'
import { Box, Grid, TextField, Typography, FormControlLabel, Switch, FormHelperText, makeStyles } from '@material-ui/core'
import { useFormik } from 'formik'
import DatePicker from 'UI/DatePicker/DatePicker'
import Button from 'UI/Button/ButtonWithPreloader'
import TipTapEditor from 'UI/TipTapEditor'
import RequiredOption from 'UI/Select/RequiredOption'
import { IChangeLogForm } from 'typescript/interfaces/changeLog'
import { uploadArticleImage } from 'services/knowledgeBase'
import convertDateForPicker from 'utils/convertDateForPicker'
import { changeLogFormSchema, hasContent } from './schema'

interface Props {
  heading: string
  roles: Array<string>
  initValues: IChangeLogForm
  loading?: boolean
  onSubmit: (values: IChangeLogForm) => void
  onCancel: () => void
}

const useStyles = makeStyles((theme) => ({
  section: {
    padding: '1.5rem',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '6px',
  },
}))

export const buildInitValues = (roles: Array<string>): IChangeLogForm => ({
  name: '',
  releaseDate: null,
  roleContents: roles.map((role) => ({ role, bodyHtml: '', isEnabled: false })),
})

const ChangeLogForm: FC<Props> = ({ heading, roles, initValues, loading, onSubmit, onCancel }) => {
  const s = useStyles()
  const { handleSubmit, values, errors, touched, handleChange, setFieldValue, submitCount } = useFormik<IChangeLogForm>({
    initialValues: initValues,
    enableReinitialize: true,
    validationSchema: changeLogFormSchema,
    onSubmit,
  })

  const setRoleContent = (index: number, field: 'bodyHtml' | 'isEnabled', value: string | boolean) =>
    setFieldValue(
      'roleContents',
      values.roleContents.map((content, contentIndex) => (contentIndex === index ? { ...content, [field]: value } : content))
    )

  return (
    <Box component="form" onSubmit={(e: React.FormEvent) => handleSubmit(e)}>
      <Typography variant="h2" color="textSecondary">
        {heading}
      </Typography>
      <Box mt="3rem">
        <Grid container spacing={8}>
          <Grid item xs={12} md={6}>
            <TextField
              name="name"
              variant="outlined"
              label={<RequiredOption label="Name" />}
              fullWidth
              value={values.name}
              onChange={handleChange}
              error={Boolean(touched.name && errors.name)}
              helperText={touched.name && errors.name}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DatePicker
              fullWidth
              label={<RequiredOption label="Release date" />}
              name="releaseDate"
              type="keyboard"
              value={values.releaseDate}
              error={Boolean(touched.releaseDate && errors.releaseDate)}
              helperText={touched.releaseDate && (errors.releaseDate as string)}
              onChange={(date) => setFieldValue('releaseDate', convertDateForPicker(date))}
            />
          </Grid>

          {values.roleContents.map((content, index) => {
            const isMissingContent = submitCount > 0 && content.isEnabled && !hasContent(content.bodyHtml)

            return (
              <Grid item xs={12} key={content.role}>
                <Box className={s.section}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h3" color="textSecondary">
                      {content.role}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={content.isEnabled}
                          onChange={(e) => setRoleContent(index, 'isEnabled', e.target.checked)}
                        />
                      }
                      label={content.isEnabled ? 'Shown' : 'Hidden'}
                    />
                  </Box>
                  {content.isEnabled && (
                    <Box mt="1.5rem">
                      <TipTapEditor
                        value={content.bodyHtml}
                        onChange={(html) => setRoleContent(index, 'bodyHtml', html)}
                        onUploadImage={uploadArticleImage}
                        error={isMissingContent}
                        helperText={isMissingContent && 'Content is required for a role section that is turned on'}
                      />
                    </Box>
                  )}
                </Box>
              </Grid>
            )
          })}

          {submitCount > 0 && Boolean(errors.roleContents) && (
            <Grid item xs={12}>
              <FormHelperText error>{errors.roleContents as string}</FormHelperText>
            </Grid>
          )}

          <Grid item xs={12}>
            <Box display="flex" alignItems="center">
              <Button loading={loading} type="submit" variant="contained" color="primary" disabled={roles.length === 0}>
                Publish
              </Button>
              <Box ml="1.5rem">
                <Button type="button" variant="text" color="primary" onClick={onCancel}>
                  Cancel
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

export default ChangeLogForm
